import { motion } from 'motion/react'
import { useNavigation } from '@/contexts/NavigationProvider'
import { useProgress } from '@/contexts/ProgressProvider'
import { GameButton } from '@/components/ui/GameButton'
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { AmbientBackdrop } from '@/components/ui/AmbientBackdrop'
import { HomeWindow } from './HomeWindow'
import { useGameAudio } from '@/contexts/GameAudioProvider'
import { COSMETICS, vipTierName } from '@/game/catalog'
import { getCharacter, RARITY_COLORS } from '@/game/characters'
import { computeDailyStatus } from '@/game/daily'
import { LEVELS } from '@/game/levels'
import { computeHomeNeglect } from '@/game/neglect'
import { HOME_DIORAMA_BG, TITLE_LOGO } from '@/game/art'
import type { ScreenId } from '@/types/game'
import type { PixelIconName } from '@/game/pixelIcons'

interface NavItem {
  screen: ScreenId
  label: string
  icon: PixelIconName
}

const NAV: NavItem[] = [
  { screen: 'map', label: 'Map', icon: 'levelSelect' },
  { screen: 'endless', label: 'Endless', icon: 'infinity' },
  { screen: 'daily', label: 'Daily', icon: 'gift' },
  { screen: 'shop', label: 'Shop', icon: 'shop' },
  { screen: 'characters', label: 'Characters', icon: 'mask' },
  { screen: 'leaderboard', label: 'Grace', icon: 'trophy' },
  { screen: 'profile', label: 'Profile', icon: 'user' },
  { screen: 'settings', label: 'Settings', icon: 'settings' },
  { screen: 'help', label: 'Help', icon: 'book' },
]

export function HomeScreen() {
  const { go, startLevel } = useNavigation()
  const { save } = useProgress()
  const audio = useGameAudio()

  const character = getCharacter(save.cosmetics.equippedCharacter)
  const accent =
    character && character.id !== 'default_hero'
      ? character.palette.base
      : (COSMETICS.find((c) => c.id === save.cosmetics.equippedSkin && c.kind === 'skin')?.color ?? '#ffb454')
  const emissive = character && character.id !== 'default_hero' ? character.palette.emissive : '#ffd27a'

  const dailyStatus = computeDailyStatus(save.meta.daily, Date.now())
  const homeNeglect = computeHomeNeglect(save.meta.home.lastReturnedHomeAt)
  const vip = save.meta.vipTier

  const completed = save.progress.completedLevels
  const nextIndex = LEVELS.find((l) => !completed.includes(l.index))?.index ?? 0
  const hasProgress = completed.length > 0
  const nextLevel = LEVELS.find((l) => l.index === nextIndex)

  const eliteHero = getCharacter('aurora_luminary')
  const ownsElite = save.cosmetics.ownedCharacters.includes('aurora_luminary')
  const showElite = eliteHero && !ownsElite

  const nav = (screen: ScreenId) => {
    audio.play('click')
    go(screen)
  }

  const neglectMessage =
    homeNeglect.state === 'restored'
      ? hasProgress
        ? 'The hearth is warm. One more path home.'
        : 'The hearth is warm. Ember is waiting to set out.'
      : homeNeglect.state === 'settled'
        ? 'The window is dimming. Slide Ember home to keep it lit.'
        : 'The light has gone tired without you. Bring Ember home to rekindle it.'

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HOME_DIORAMA_BG})`, opacity: 0.34 * homeNeglect.light, filter: 'saturate(0.9)' }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'linear-gradient(180deg, rgba(10,8,16,0.4) 0%, rgba(10,8,16,0.55) 45%, var(--ink) 88%)' }}
        aria-hidden="true"
      />
      <AmbientBackdrop accent={accent} />
      <header className="relative flex items-center justify-between gap-2 px-4 pt-4 pb-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <img
            src={TITLE_LOGO}
            alt="Take Me Home"
            draggable={false}
            className="h-9 w-9 shrink-0 object-contain"
            style={{ filter: `drop-shadow(0 0 10px ${accent}55)` }}
          />
          <span className="whitespace-nowrap font-display font-black text-lg tracking-tight">Take Me Home</span>
          {vip > 0 && (
            <span
              className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black"
              style={{ background: 'var(--coin)', color: '#231404' }}
              title={`VIP: ${vipTierName(vip)}`}
            >
              <PixelIcon name="vip" size={11} />
              {vipTierName(vip)}
            </span>
          )}
        </div>
        <CurrencyDisplay coins={save.economy.coins} gems={save.economy.gems} lives={save.economy.lives} />
      </header>

      <div className="relative flex-1 min-h-0 flex flex-col items-center justify-center px-6 py-4">
        <div className="w-full max-w-md flex flex-col items-center gap-6">
          <HomeWindow
            accent={accent}
            emissive={emissive}
            shape={character?.shape}
            equippedProps={save.cosmetics.equippedProps}
            neglect={homeNeglect}
            characterId={character?.id ?? 'default_hero'}
          />

          <motion.p
            key={neglectMessage}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="-mt-1 flex items-center gap-2 text-center text-sm font-medium text-[var(--text-dim)]"
          >
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ background: accent, boxShadow: `0 0 8px ${accent}`, opacity: homeNeglect.light }}
            />
            {neglectMessage}
          </motion.p>

          {showElite && eliteHero && (
            <motion.button
              onClick={() => nav('shop')}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="group relative flex w-full items-center gap-3 overflow-hidden rounded-[var(--r-lg)] px-4 py-3 text-left shadow-raised"
              style={{
                background: `linear-gradient(120deg, ${eliteHero.palette.accent}33, var(--surface-raised) 65%)`,
                border: `1px solid ${RARITY_COLORS[eliteHero.rarity]}66`,
              }}
            >
              <motion.span
                className="relative h-12 w-12 shrink-0 rounded-full"
                style={{
                  background: `radial-gradient(circle at 35% 30%, ${eliteHero.palette.emissive}, ${eliteHero.palette.base} 65%)`,
                  boxShadow: `0 0 18px ${eliteHero.palette.base}88`,
                }}
                animate={{ scale: [1, 1.06, 1] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-black uppercase tracking-wide" style={{ color: RARITY_COLORS[eliteHero.rarity] }}>
                    Legendary
                  </span>
                  <PixelIcon name="vip" size={11} className="text-[var(--coin)]" />
                </div>
                <div className="font-display text-base font-black leading-tight">{eliteHero.name}</div>
                <div className="text-xs text-[var(--text-dim)] leading-snug">Prestige aurora hero — unlock in the store</div>
              </div>
              <PixelIcon name="arrow-left" size={16} className="shrink-0 rotate-180 text-[var(--text-faint)] transition group-hover:text-[var(--ember)]" />
            </motion.button>
          )}

          <div className="w-full flex flex-col gap-2.5">
            <GameButton
              size="lg"
              icon={hasProgress ? 'continue' : 'play'}
              fullWidth
              onClick={() => (hasProgress ? startLevel(nextIndex) : go('map'))}
            >
              {hasProgress ? `Continue · ${nextLevel?.name ?? 'Play'}` : 'Play'}
            </GameButton>
            {hasProgress && (
              <GameButton variant="secondary" size="md" icon="levelSelect" fullWidth onClick={() => nav('map')}>
                World Map
              </GameButton>
            )}
          </div>
        </div>
      </div>

      <nav className="relative hidden sm:grid grid-cols-3 gap-2 px-4 pb-[calc(1.25rem+var(--safe-bottom))] w-full max-w-md mx-auto">
        {NAV.map((item) => (
          <motion.button
            key={item.screen + item.label}
            onClick={() => nav(item.screen)}
            whileTap={{ scale: 0.96 }}
            className="relative flex flex-col items-center gap-1 rounded-[var(--r-md)] bg-[var(--surface-raised)] shadow-emboss py-3 text-[var(--text-dim)] hover:text-[var(--text)] hover:-translate-y-0.5 transition"
          >
            <PixelIcon name={item.icon} size={22} />
            <span className="text-xs font-semibold">{item.label}</span>
            {item.screen === 'daily' && dailyStatus.canClaim && (
              <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-[var(--ember)] shadow-glow" />
            )}
          </motion.button>
        ))}
      </nav>
    </div>
  )
}
